import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createTenantUser } from "../../../services/TenantsService";
import { toast } from "react-toastify";
import Select from "react-select";
import { DatePicker } from "rsuite";
import {
  createAndUpdateCoach,
  getCoachSkill,
  uploadPlayerForm,
} from "../../../services/academicService";
import ImagePreviewModal from "../../components/ImagePreviewModal";
import { formatDate } from "../../../utils/helperFunction";

const AddCoach = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editUser = location.state?.user || null;
  const mode = location.state?.mode || "add";
  const isEditMode = mode === "edit" && !!editUser;
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [skill, setSkill] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    dob: null,
    experience: "",
    qualification: "",
    email: "",
    phone: "",
    about: "",
  });
  const [uploadedImageFiles, setUploadedImageFiles] = useState(null);
  const [uploadedFormFiles, setUploadedFormFiles] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileName, setPreviewFileName] = useState("");

  // for all inputs
  useEffect(() => {
    if (isEditMode && editUser) {
      setFormData({
        name: editUser.name || "",
        dob: editUser.dob ? new Date(editUser.dob) : null,
        experience: editUser.experience || "",
        qualification: editUser.qualification || "",
        email: editUser.email || "",
        phone: editUser.contactPhone || "",
        about: editUser.about || "",
      });
      setSelectedSkill(
        editUser.coachSkill
          ? { value: editUser.coachSkill, label: editUser.coachSkill }
          : null,
      );
      setUploadedFormFiles(editUser.coachImage || null);
    }
  }, [isEditMode, editUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.dob) newErrors.dob = "Date of Birth is required";
    if (!formData.experience) newErrors.experience = "Experience is required";
    if (!formData.qualification)
      newErrors.qualification = "Qualification is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (
      !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(formData.email)
    ) {
      newErrors.email = "* Invalid email format";
    }
    if (!formData.phone) newErrors.phone = "Mobile No is required";
    if (!selectedSkill) newErrors.skill = "Skill is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // return true if no errors
  };

  const handleFormFileChange = async (e) => {
    try {
      const file = e.target.files[0]; //single file
      if (!file) return;

      //Upload to API
      const res = await uploadPlayerForm(file);

      //REPLACE instead of append
      setUploadedFormFiles(res);
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;
    setLoading(true);
    const payload = {
      name: formData.name,
      dob: formData.dob ? formatDate(formData.dob) : null,
      experience: formData.experience,
      qualification: formData.qualification,
      coachSkill: selectedSkill.value,
      email: formData.email,
      contactPhone: formData.phone,
      coachImage: uploadedFormFiles,
      about: formData.about,
      ...(isEditMode ? { id: editUser.id } : { password: formData.password }),
    };

    try {
      if (isEditMode) {
        const res = await createAndUpdateCoach(payload); // id in URL + in body
        if (res) {
          toast.success("Coach Updated successfully!", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      } else {
        const res = await createAndUpdateCoach(payload);
        if (res) {
          toast.success("Coach created successfully!", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      }

      navigate("/admin/all-coaches");
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditMode && editUser) {
      // Reset back to original edit data
      setFormData({
        name: editUser.name || "",
        dob: editUser.dob || "",
        experience: editUser.experience || "",
        qualification: editUser.qualification || "",
        skill: editUser.skill || "",
        email: editUser.email || "",
        phone: editUser.phone || "",
      });
      setSelectedSkill(editUser.skill || null);
    } else {
      // Reset to empty for add mode
      setFormData({
        name: "",
        dob: "",
        experience: "",
        qualification: "",
        email: "",
        phone: "",
      });
      setSelectedSkill(null);
    }
  };

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  useEffect(() => {
    fetchCoachSkill();
  }, []);

  async function fetchCoachSkill() {
    try {
      const res = await getCoachSkill();
      setSkill(res);
    } catch (error) {
      console.error("Error fetching coach skills:", error);
    }
  }

  return (
    <>
      <div className="page-titles d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{isEditMode ? "Edit" : "Add"} Coaches</h4>
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item ">
            <Link to="/admin/all-coaches">Coaches</Link>
          </li>
          <li className="breadcrumb-item active">
            <Link to={"#"}>{isEditMode ? "Edit Coach" : "Add Coach"}</Link>
          </li>
        </ol>
      </div>
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">{isEditMode ? "Edit" : "Add"} User</h5>
            </div>
            <div className="card-body">
              <form>
                <div className="row">
                  {/* Name of the Student */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="name">
                        Name of the Coach <span className="text-danger">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        placeholder="Enter Full Name"
                        type="text"
                        className={`form-control ${errors.name ? "is-invalid" : ""
                          }`}
                        required
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="dob">
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <div className="input-hasicon mb-xl-0 mb-3">
                        <DatePicker
                          placeholder="Select Date of Birth"
                          className="picker-suit"
                          value={formData.dob}
                          onChange={(date) => {
                            handleDateChange(date, "dob");
                            setErrors((prev) => ({
                              ...prev,
                              dob: "",
                            }));
                          }}
                        />
                        <div className="icon">
                          <i className="far fa-calendar" />
                        </div>
                      </div>
                      {errors.dob && (
                        <div className="text-danger mt-1">{errors.dob}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">
                        Email
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        id="email"
                        placeholder="Enter Email"
                        type="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""
                          }`}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        readOnly={isEditMode}
                        required
                      />
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="phone">
                        Mobile No
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        id="phone"
                        placeholder="Enter Mobile No"
                        type="number"
                        className={`form-control ${errors.phone ? "is-invalid" : ""
                          }`}
                        required
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                      {errors.phone && (
                        <div className="invalid-feedback">{errors.phone}</div>
                      )}
                    </div>
                  </div>

                  {/* Experience */}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="experience">
                        Experience (in years){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        id="experience"
                        placeholder="Enter Experience"
                        type="number"
                        required
                        name="experience"
                        value={formData.experience}
                        className={`form-control ${errors.experience ? "is-invalid" : ""
                          }`}
                        onChange={handleChange}
                      />
                      {errors.experience && (
                        <div className="invalid-feedback">
                          {errors.experience}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Qualification */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="qualification">
                        Qualification <span className="text-danger">*</span>
                      </label>
                      <input
                        id="qualification"
                        placeholder="Enter Qualification"
                        type="text"
                        className={`form-control ${errors.qualification ? "is-invalid" : ""
                          }`}
                        required
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                      />
                      {errors.qualification && (
                        <div className="invalid-feedback">
                          {errors.qualification}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* skill like batting , bowling  */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="skill">
                        Skill <span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        options={skill}
                        placeholder="Select Skill"
                        className="custom-react-select"
                        value={selectedSkill}
                        onChange={(selectedOption) => {
                          setSelectedSkill(selectedOption);
                          setErrors((prev) => ({
                            ...prev,
                            skill: "",
                          }));
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.skill
                              ? "#FF1616"
                              : base.borderColor,
                            boxShadow: errors.skill
                              ? "0 0 0 1px #FF1616"
                              : base.boxShadow,
                            "&:hover": {
                              borderColor: errors.skill
                                ? "#FF1616"
                                : base.borderColor,
                            },
                          }),
                        }}
                      />
                      {errors.skill && (
                        <div className="invalid-feedback d-block">
                          {errors.skill}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="about">
                        About
                      </label>
                      <input
                        id="about"
                        placeholder="Enter About"
                        type="text"
                        className="form-control"
                        required
                        name="about"
                        value={formData.about}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group fallback w-100">
                      <label className="form-label">Coach Image</label>
                      <input
                        id="coachImage"
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleFormFileChange}
                      />
                    </div>
                  </div>
                  <div className="col-sm-12 flex-row d-flex gap-4">
                    {uploadedImageFiles && (
                      <div className="col-sm-6">
                        <h6>Upload Image</h6>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                            {/* File Name on the Left */}
                            <div
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {uploadedImageFiles?.fileName}
                            </div>

                            {/* Action Buttons on the Right */}
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                title="View"
                                onClick={() => {
                                  setPreviewFileName(
                                    uploadedImageFiles.fileName,
                                  );
                                  setPreviewModal(true);
                                }}
                              >
                                <i className="fa fa-eye" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                title="Delete"
                                onClick={() => setUploadedImageFiles(null)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {uploadedFormFiles && (
                      <div className="col-sm-6">
                        <h6>Image File</h6>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                            {/* File Name on the Left */}
                            <div
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {uploadedFormFiles?.fileName}
                            </div>

                            {/* Action Buttons on the Right */}
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                title="View"
                                onClick={() => {
                                  setPreviewFileName(
                                    uploadedFormFiles.fileName,
                                  );
                                  setPreviewModal(true);
                                }}
                              >
                                <i className="fa fa-eye" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                title="Delete"
                                onClick={() => setUploadedFormFiles(null)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit & Cancel */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="btn btn-primary me-1"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                    <Link
                      to="/admin/all-coaches"
                      className="btn btn-danger light"
                      onClick={handleReset}
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ImagePreviewModal
        show={previewModal}
        onClose={() => setPreviewModal(false)}
        fileName={previewFileName}
      />
    </>
  );
};

export default AddCoach;
